import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Gem, Check, AlertTriangle, Loader2, ArrowRight, ShieldCheck } from "lucide-react";

const Shell = ({ children, title, sub }) => (
  <div className="min-h-screen flex items-center justify-center p-6">
    <div className="glass-panel rounded-3xl p-10 lg:p-14 max-w-lg w-full text-center relative overflow-hidden" data-testid="verify-email-page">
      <div className="crystal-glow opacity-50" />
      <div className="relative">
        <div className="flex justify-center mb-6">
          <Gem className="w-10 h-10 text-gold" />
        </div>
        <h1 className="font-display text-3xl text-white mb-2">{title}</h1>
        <p className="text-zinc-400 mb-8">{sub}</p>
        {children}
      </div>
    </div>
  </div>
);

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { refresh } = useAuth();
  const [state, setState] = useState("confirm"); // confirm | verifying | success | already | error
  const [error, setError] = useState("");

  if (!token) {
    return (
      <Shell title="Invalid link" sub="This verification link is missing its token.">
        <div className="w-14 h-14 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center mx-auto mb-6">
          <AlertTriangle className="w-7 h-7 text-amber-400" />
        </div>
        <Link to="/login" className="btn-primary" data-testid="verify-error-login">
          Sign in <ArrowRight className="w-4 h-4" />
        </Link>
      </Shell>
    );
  }

  const doVerify = async () => {
    setState("verifying");
    setError("");
    try {
      const { data } = await api.post(`/auth/verify-email`, { token });
      try { await refresh(); } catch { /* refresh is best-effort */ }
      setState(data.already_verified ? "already" : "success");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed");
      setState("error");
    }
  };

  if (state === "confirm") {
    return (
      <Shell title="Verify your email" sub="Click the button below to activate your My Leads Convert account.">
        <div className="w-14 h-14 rounded-full bg-yellow-300/15 border border-yellow-300/40 flex items-center justify-center mx-auto mb-6">
          <ShieldCheck className="w-7 h-7 text-gold" />
        </div>
        <button onClick={doVerify} className="btn-primary" data-testid="verify-confirm-button">
          Click to verify <ArrowRight className="w-4 h-4" />
        </button>
        <p className="text-xs text-zinc-500 mt-6 leading-relaxed">
          This extra click keeps mail-scanning bots from accidentally activating your account.
        </p>
      </Shell>
    );
  }

  if (state === "verifying") {
    return (
      <Shell title="Verifying your email" sub="One moment...">
        <Loader2 className="w-8 h-8 text-gold mx-auto animate-spin" />
      </Shell>
    );
  }

  if (state === "success" || state === "already") {
    return (
      <Shell
        title={state === "already" ? "Already verified" : "Email verified"}
        sub={state === "already" ? "Your account is already active." : "You're all set. Welcome to My Leads Convert."}
      >
        <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto mb-6">
          <Check className="w-7 h-7 text-emerald-400" />
        </div>
        <Link to="/dashboard" className="btn-primary" data-testid="verified-go-dashboard">
          Open dashboard <ArrowRight className="w-4 h-4" />
        </Link>
      </Shell>
    );
  }

  return (
    <Shell title="Verification failed" sub={error}>
      <div className="w-14 h-14 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center mx-auto mb-6">
        <AlertTriangle className="w-7 h-7 text-amber-400" />
      </div>
      <p className="text-sm text-zinc-400 mb-6">
        The link may have expired or already been used. Sign in to request a fresh verification email.
      </p>
      <Link to="/login" className="btn-primary" data-testid="verify-error-login">
        Sign in <ArrowRight className="w-4 h-4" />
      </Link>
    </Shell>
  );
}
